import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../../context/app';
import { PrintContext } from '../../../context/print';
import type { NavStep, Site } from '../../../context/types';
import { useFormattedWalkingInstruction } from '../../../hooks/useFormattedWalkingInstruction';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { getPinCoordinate } from '../../../utils/getPinCoordinates';
import { ensureHttps } from '../../../utils/url';
import ButtonStyled from '../button/Button';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import { IconWithCoordinates } from '../youAreHerePin/IconWithCoordinates';
import styles from './LegTwoVisualStep.module.scss';
import { LegTwoVisualStepMobile } from './LegTwoVisualStepMobile';
import { LegTwoVisualStepPrint } from './LegTwoVisualStepPrint';

interface LegTwoVisualStepProps {
  step: NavStep;
  index: number;
  isLastStep?: boolean;
  isFirstStep?: boolean;
  site?: Site;
  nextStep?: NavStep | undefined;
  onExit?: () => void;
  isCtsStep?: boolean;
}
export const LegTwoVisualStep: React.FC<LegTwoVisualStepProps> = ({
  step,
  index,
  isLastStep,
  isFirstStep,
  site,
  onExit,
  isCtsStep,
  nextStep,
}) => {
  const {
    setLegTwoFocusedStep,
    setLegToggle,
    directions,
    showPrintDirectionView,
  } = useContext(AppContext);
  const { t } = useTranslation('common');
  const { kiosk, kioskMode } = useKioskManager();

  const [imgWidth, setImgWidth] = useState(0);
  const [imgHeight, setImgHeight] = useState(0);
  const mapImageRef = useRef<HTMLImageElement | null>(null);
  const formattedInstruction = useFormattedWalkingInstruction(step, nextStep);
  const { printImgWidth, printImgHeight, printDialogOpen } =
    useContext(PrintContext);
  const isMobile = useIsMobile();

  const selectedBuilding = useMemo(
    () =>
      site?.buildings.find((building) => building.id === step.polyLine[0].b),
    [site?.buildings, step.polyLine],
  );

  const selectedFloor = useMemo(
    () =>
      selectedBuilding?.floors
        .sort((a, b) => a.id - b.id)
        .find((floor) => floor.id === step.polyLine[0].f),
    [selectedBuilding?.floors, step.polyLine],
  );
  const map = useMemo(
    () => ensureHttps(selectedFloor?.map_url ?? site?.campus_map.map_url),
    [selectedFloor?.map_url, site?.campus_map.map_url],
  );

  const svgPath = useMemo(() => {
    if (!step) return '';
    const translatedPoints = step.polyLine.map((point) =>
      getPinCoordinate(
        point.x,
        point.y,
        selectedFloor,
        site,
        printDialogOpen ? printImgHeight : imgHeight,
        printDialogOpen ? printImgWidth : imgWidth,
      ),
    );
    let path = `M ${translatedPoints[0].x} ${translatedPoints[0].y}`;
    for (let i = 1; i < translatedPoints.length; i++) {
      const point = translatedPoints[i];
      path += ` L ${point.x} ${point.y}`;
    }
    return path;
  }, [
    imgHeight,
    imgWidth,
    selectedFloor,
    site,
    step,
    printImgWidth,
    printImgHeight,
    printDialogOpen,
  ]);

  const startingPoint: { x: number; y: number } = useMemo(() => {
    if (!step) return { x: 0, y: 0 };
    return getPinCoordinate(
      step.polyLine[0].x,
      step.polyLine[0].y,
      selectedFloor,
      site,
      printDialogOpen ? printImgHeight : imgHeight,
      printDialogOpen ? printImgWidth : imgWidth,
    );
  }, [
    imgHeight,
    imgWidth,
    selectedFloor,
    site,
    step,
    printImgWidth,
    printImgHeight,
    printDialogOpen,
  ]);

  const endingPoint: { x: number; y: number } = useMemo(() => {
    if (!step) return { x: 0, y: 0 };
    return getPinCoordinate(
      step.polyLine[step.polyLine.length - 1].x,
      step.polyLine[step.polyLine.length - 1].y,
      selectedFloor,
      site,
      printDialogOpen ? printImgHeight : imgHeight,
      printDialogOpen ? printImgWidth : imgWidth,
    );
  }, [
    imgHeight,
    imgWidth,
    selectedFloor,
    site,
    step,
    printImgWidth,
    printImgHeight,
    printDialogOpen,
  ]);

  const goToPreviousStep = () => {
    if (index === 0 && directions) {
      setLegToggle(false);
    } else {
      setLegTwoFocusedStep(index - 1);
    }
  };

  const goToNextStep = () => {
    if (isLastStep) {
      onExit && onExit();
      return;
    }
    setLegTwoFocusedStep(index + 1);
  };

  const onMapLoad = (event: any) => {
    setImgWidth(event.target.offsetWidth);
    setImgHeight(event.target.offsetHeight);
  };
  const handleResize = () => {
    if (mapImageRef.current) {
      setImgWidth(mapImageRef.current.offsetWidth);
      setImgHeight(mapImageRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  }, []);

  const showYouAreHere = useMemo(
    () => Boolean(isFirstStep && kioskMode),
    [isFirstStep, kioskMode],
  );

  const createArrowPaths = (
    svgPath: string,
    arrowStartLength: number,
    arrowEndLength: number,
  ): { startArrowPath: string; endArrowPath: string } => {
    // Parse the path to get all commands
    const commands = svgPath.split('L').map((command, index) => {
      const coords = command.trim().split(' ');
      if (index === 0) {
        // First command starts with 'M'
        return {
          command: 'M',
          x: parseFloat(coords[1]),
          y: parseFloat(coords[2]),
        };
      }
      return {
        command: 'L',
        x: parseFloat(coords[0]),
        y: parseFloat(coords[1]),
      };
    });

    if (commands.length < 2) {
      throw new Error('SVG path must contain at least two points.');
    }

    // Calculate vector for the first segment
    const firstSegment = {
      dx: commands[1].x - commands[0].x,
      dy: commands[1].y - commands[0].y,
    };
    // Normalize the vector
    const firstSegmentLength = Math.sqrt(
      firstSegment.dx ** 2 + firstSegment.dy ** 2,
    );
    const firstNormalized = {
      dx: (firstSegment.dx / firstSegmentLength) * arrowStartLength,
      dy: (firstSegment.dy / firstSegmentLength) * arrowStartLength,
    };

    // Adjust the start point
    const newStart = {
      x: commands[0].x + firstNormalized.dx,
      y: commands[0].y + firstNormalized.dy,
    };
    const startArrowPath = `M${commands[0].x},${commands[0].y} L${newStart.x},${newStart.y}`;

    // Calculate vector for the last segment
    const lastSegment = {
      dx: commands[commands.length - 1].x - commands[commands.length - 2].x,
      dy: commands[commands.length - 1].y - commands[commands.length - 2].y,
    };
    // Normalize the vector
    const lastSegmentLength = Math.sqrt(
      lastSegment.dx ** 2 + lastSegment.dy ** 2,
    );
    const lastNormalized = {
      dx: (lastSegment.dx / lastSegmentLength) * arrowEndLength,
      dy: (lastSegment.dy / lastSegmentLength) * arrowEndLength,
    };

    // Adjust the end point
    const newEnd = {
      x: commands[commands.length - 1].x - lastNormalized.dx,
      y: commands[commands.length - 1].y - lastNormalized.dy,
    };
    const endArrowPath = `M${newEnd.x},${newEnd.y} L${
      commands[commands.length - 1].x
    },${commands[commands.length - 1].y}`;

    return { startArrowPath, endArrowPath };
  };

  const arrowStartOffsetLength = 10;
  const arrowEndOffsetLength = 25;
  const { startArrowPath, endArrowPath } = createArrowPaths(
    svgPath,
    arrowStartOffsetLength,
    arrowEndOffsetLength,
  );

  return isMobile ? (
    <LegTwoVisualStepMobile
      map={map}
      ref={mapImageRef}
      imgHeight={imgHeight}
      imgWidth={imgWidth}
      onMapLoad={onMapLoad}
      isLastStep={isLastStep}
      index={index}
      endArrowPath={endArrowPath}
      endingPoint={endingPoint}
      goToPreviousStep={goToPreviousStep}
      goToNextStep={goToNextStep}
      showYouAreHere={showYouAreHere}
      startArrowPath={startArrowPath}
      formattedInstruction={formattedInstruction}
      svgPath={svgPath}
      startingPoint={startingPoint}
    />
  ) : showPrintDirectionView ? (
    <LegTwoVisualStepPrint
      map={map}
      ref={mapImageRef}
      onMapLoad={onMapLoad}
      showYouAreHere={showYouAreHere}
      startingPoint={startingPoint}
      endingPoint={endingPoint}
      isLastStep={isLastStep}
      svgPath={svgPath}
      startArrowPath={startArrowPath}
      endArrowPath={endArrowPath}
      index={index}
      formattedInstruction={formattedInstruction}
      imgHeight={imgHeight}
      imgWidth={imgWidth}
    />
  ) : (
    <div
      className={`${styles.stepContainer}  ${
        kiosk && kioskMode && styles[kiosk.model]
      }`}
    >
      <div className={styles.mapImageWrapper}>
        {/* TODO: May want to implement a loading state here since the campus map image is large */}
        <img
          className={`${styles.mapImg}`}
          src={map}
          ref={mapImageRef}
          onLoad={onMapLoad}
          alt={'stepDirections'}
        />
        {showYouAreHere && (
          <IconWithCoordinates
            coordinates={startingPoint}
            iconType={IconType.YOU_ARE_HERE}
            size={125}
          />
        )}
        {isLastStep && (
          <IconWithCoordinates
            coordinates={endingPoint}
            iconType={IconType.PIN_ACTIVE}
            size={50}
          />
        )}
        <svg
          className={styles.overlay}
          height={mapImageRef?.current?.offsetHeight}
          width={mapImageRef?.current?.offsetWidth}
        >
          {!showYouAreHere && (
            <circle
              cx={startingPoint.x}
              cy={startingPoint.y}
              r="10"
              fill="white"
              stroke="black"
              strokeWidth="4"
            />
          )}
          {!isLastStep && (
            <circle
              cx={endingPoint.x}
              cy={endingPoint.y}
              r="10"
              fill="#0D6AFF"
              stroke="#0D6AFF"
              strokeWidth="4"
            />
          )}
          <path
            id="any"
            d={svgPath}
            stroke="#0D6AFF"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="12 12"
            strokeMiterlimit="10"
            fill="none"
            strokeDashoffset="1"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="100;0"
              dur="3s"
              calcMode="linear"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>

      {isCtsStep ? (
        <div className={styles.ctsStepFooter}>
          <div className={styles.ctsStepNumber}>
            <div className={styles.ctsStepNumberText}>{index + 1}</div>
          </div>
          <h2 className={styles.instruction}>{formattedInstruction}</h2>
        </div>
      ) : (
        <div
          className={`${styles.buttonContainer} ${
            kiosk && kioskMode && styles[kiosk.model]
          }`}
        >
          <ButtonStyled
            onClick={goToPreviousStep}
            disabled={index - 1 <= -1 && !directions}
            uppercase
            className={styles.buttonLeft}
          >
            <Icon icon={IconType.LEFT} size={32} />
            {t('campusNavigation.directions.previous')}
          </ButtonStyled>
          <h2 className={styles.instruction}>
            {index + 1}. {formattedInstruction}
          </h2>
          <ButtonStyled
            onClick={goToNextStep}
            uppercase
            className={styles.buttonRight}
          >
            {isLastStep
              ? t('campusNavigation.directions.exit')
              : t('campusNavigation.directions.continue')}
            {!isLastStep && <Icon icon={IconType.RIGHT} size={32} />}
          </ButtonStyled>
        </div>
      )}
    </div>
  );
};
